import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';

const GET_ALL_AMENITIES = gql`
  query GetAllAmenities {
    allAmenities {
      id
      streamlineAmenityId
      name
      groupName
      showOnApp
    }
  }
`;

const UPDATE_AMENITY_VISIBILITY = gql`
  mutation UpdateAmenityVisibility($id: ID!, $showOnApp: Boolean!) {
    updateAmenityVisibility(id: $id, showOnApp: $showOnApp) {
      id
      showOnApp
    }
  }
`;

const UPDATE_BULK_AMENITY_VISIBILITY = gql`
  mutation UpdateBulkAmenityVisibility($ids: [ID!]!, $showOnApp: Boolean!) {
    updateBulkAmenityVisibility(ids: $ids, showOnApp: $showOnApp)
  }
`;

function GuestAppSettingsSection() {
  const { data, loading, error, refetch } = useQuery(GET_ALL_AMENITIES);
  const [updateAmenity] = useMutation(UPDATE_AMENITY_VISIBILITY);
  const [updateBulk, { loading: bulkLoading }] = useMutation(UPDATE_BULK_AMENITY_VISIBILITY);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const allAmenities = data?.allAmenities ?? [];
  const filtered = searchTerm
    ? allAmenities.filter(a => a.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : allAmenities;
  const amenitiesByGroup = filtered.reduce((acc, amenity) => {
    const group = amenity.groupName || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(amenity);
    return acc;
  }, {});
  const groupNames = Object.keys(amenitiesByGroup).sort();

  const totalCount = allAmenities.length;
  const enabledCount = allAmenities.filter(a => a.showOnApp).length;

  const handleToggle = async (amenity) => {
    try {
      await updateAmenity({
        variables: { id: amenity.id, showOnApp: !amenity.showOnApp },
        refetchQueries: [{ query: GET_ALL_AMENITIES }],
        awaitRefetchQueries: false,
      });
    } catch {
      toast.error('Failed to update amenity');
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const handleSelectAllInGroup = async (groupName, select) => {
    const ids = amenitiesByGroup[groupName].map(a => a.id);
    try {
      await updateBulk({ variables: { ids, showOnApp: select } });
      await refetch();
      toast.success(`${select ? 'Enabled' : 'Disabled'} all in ${groupName}`);
    } catch {
      toast.error('Failed to update amenities');
    }
  };

  const handleSelectAll = async (select) => {
    const ids = data.allAmenities.map(a => a.id);
    try {
      await updateBulk({ variables: { ids, showOnApp: select } });
      await refetch();
      toast.success(`${select ? 'Enabled' : 'Disabled'} all amenities`);
    } catch {
      toast.error('Failed to update amenities');
    }
  };

  if (loading) return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="text-gray-500">Loading amenities...</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="text-red-600">Error loading amenities: {error.message}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Amenities Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-medium text-gray-900">Guest App Amenities</h2>
          <span className="text-sm text-gray-500">
            {enabledCount} of {totalCount} enabled
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Select which amenities are visible to guests in the mobile app. Only checked amenities will appear in the Property Info section.
        </p>

        {/* Search + Bulk Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="relative flex-1 w-full">
            <input
              type="text"
              placeholder="Search amenities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm pl-9 pr-3 py-2 text-sm focus:border-sky-500 focus:ring-sky-500"
            />
            <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleSelectAll(true)}
              disabled={bulkLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Enable All
            </button>
            <button
              onClick={() => handleSelectAll(false)}
              disabled={bulkLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Disable All
            </button>
          </div>
        </div>

        {/* Accordion Groups */}
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
          {groupNames.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">
              {searchTerm ? 'No amenities match your search.' : 'No amenities found. Run an amenities sync first.'}
            </div>
          )}
          {groupNames.map((groupName) => {
            const groupAmenities = amenitiesByGroup[groupName];
            const isExpanded = expandedGroups[groupName] ?? false;
            const groupEnabled = groupAmenities.filter(a => a.showOnApp).length;

            return (
              <div key={groupName}>
                {/* Group Header */}
                <button
                  onClick={() => handleToggleGroup(groupName)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">{groupName}</span>
                    <span className="text-xs text-gray-500">({groupAmenities.length})</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    groupEnabled === groupAmenities.length
                      ? 'bg-green-100 text-green-700'
                      : groupEnabled > 0
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}>
                    {groupEnabled}/{groupAmenities.length}
                  </span>
                </button>

                {/* Group Body */}
                {isExpanded && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    {/* Group-level select all/none */}
                    <div className="flex gap-2 px-4 py-2 border-b border-gray-200">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSelectAllInGroup(groupName, true); }}
                        disabled={bulkLoading}
                        className="text-xs text-sky-600 hover:text-sky-800 font-medium disabled:opacity-50"
                      >
                        Select All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSelectAllInGroup(groupName, false); }}
                        disabled={bulkLoading}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50"
                      >
                        Deselect All
                      </button>
                    </div>

                    {/* Amenity Checkboxes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
                      {groupAmenities.map((amenity) => (
                        <label
                          key={amenity.id}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={amenity.showOnApp}
                            onChange={() => handleToggle(amenity)}
                            className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                          />
                          <span className="text-sm text-gray-700">{amenity.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GuestAppSettingsSection;
